import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, X, TrendingUp, Activity, Zap } from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Subscriptions = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      fetchData();
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchData = async () => {
    try {
      const [subsRes, metricsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/subscriptions`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/dashboard/metrics`, { withCredentials: true })
      ]);
      setSubscriptions(subsRes.data);
      
      // Create metrics map
      const metricsMap = {};
      metricsRes.data.service_metrics?.forEach(m => {
        metricsMap[m.subscription_id] = m;
      });
      setMetrics(metricsMap);
    } catch (error) {
      toast.error('Failed to fetch subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (subscriptionId) => {
    if (!window.confirm('Are you sure you want to cancel this subscription?')) {
      return;
    }

    setCancelling(subscriptionId);
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/subscriptions/${subscriptionId}`,
        { withCredentials: true }
      );
      
      toast.success('Subscription cancelled successfully');
      fetchData();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error('Failed to cancel subscription');
    } finally {
      setCancelling(null);
    }
  };

  const getBillingCycleLabel = (cycle) => {
    const labels = {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      yearly: 'Yearly',
      'one-time': 'One-Time'
    };
    return labels[cycle] || cycle;
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground font-mono">Loading...</p>
        </div>
      </div>
    );
  }

  const activeSubscriptions = subscriptions.filter(s => s.status === 'active');
  const cancelledSubscriptions = subscriptions.filter(s => s.status === 'cancelled');

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 w-full border-b border-border backdrop-blur-xl bg-background/80">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex h-16 items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            <div className="font-black text-xl uppercase tracking-tight">
              NEXT<span className="text-primary">/</span>GEN
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 md:px-12 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl sm:text-5xl font-black uppercase mb-2">
            MY SUBSCRIPTIONS
          </h1>
          <p className="text-muted-foreground mb-12">Manage your active and cancelled service subscriptions</p>

          {/* Active Subscriptions */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold uppercase mb-6">Active Services ({activeSubscriptions.length})</h2>
            
            {activeSubscriptions.length === 0 ? (
              <Card className="p-12 text-center border-border">
                <p className="text-muted-foreground">No active subscriptions</p>
                <Button className="mt-4" onClick={() => navigate('/services')}>
                  Browse Services
                </Button>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {activeSubscriptions.map((subscription, index) => {
                  const subMetrics = metrics[subscription.subscription_id];
                  
                  return (
                    <motion.div
                      key={subscription.subscription_id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                    >
                      <Card className="p-6 border-border hover:border-primary/50 transition-colors">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <h3 className="text-xl font-bold mb-1">{subscription.service_name}</h3>
                            <div className="flex items-center gap-2 mb-3">
                              <Badge className="bg-primary text-primary-foreground">
                                {subscription.status.toUpperCase()}
                              </Badge>
                              <span className="text-sm text-muted-foreground">
                                {getBillingCycleLabel(subscription.billing_cycle)}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-black text-primary">
                              ${subscription.price.toFixed(2)}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              /{subscription.billing_cycle}
                            </div>
                          </div>
                        </div>

                        {/* Usage Metrics */}
                        {subMetrics && (
                          <div className="grid grid-cols-3 gap-4 mb-4 p-4 bg-muted/30 rounded-sm">
                            <div className="text-center">
                              <Activity className="w-5 h-5 text-primary mx-auto mb-1" />
                              <div className="text-lg font-bold">{subMetrics.uptime_percentage}%</div>
                              <div className="text-xs text-muted-foreground">Uptime</div>
                            </div>
                            <div className="text-center">
                              <TrendingUp className="w-5 h-5 text-primary mx-auto mb-1" />
                              <div className="text-lg font-bold">{subMetrics.total_requests.toLocaleString()}</div>
                              <div className="text-xs text-muted-foreground">Requests</div>
                            </div>
                            <div className="text-center">
                              <Zap className="w-5 h-5 text-primary mx-auto mb-1" />
                              <div className="text-lg font-bold">{subMetrics.avg_response_time_ms.toFixed(0)}ms</div>
                              <div className="text-xs text-muted-foreground">Avg Response</div>
                            </div>
                          </div>
                        )}

                        <div className="space-y-2 text-sm text-muted-foreground mb-4">
                          <div>
                            <span className="font-semibold">Started:</span> {format(new Date(subscription.start_date), 'MMM dd, yyyy')}
                          </div>
                          {subscription.next_billing_date && (
                            <div>
                              <span className="font-semibold">Next Billing:</span> {format(new Date(subscription.next_billing_date), 'MMM dd, yyyy')}
                            </div>
                          )}
                        </div>

                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleCancel(subscription.subscription_id)}
                          disabled={cancelling === subscription.subscription_id}
                          className="w-full"
                        >
                          {cancelling === subscription.subscription_id ? (
                            'Cancelling...'
                          ) : (
                            <>
                              <X className="w-4 h-4 mr-2" />
                              Cancel Subscription
                            </>
                          )}
                        </Button>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Cancelled Subscriptions */}
          {cancelledSubscriptions.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold uppercase mb-6">Cancelled Services ({cancelledSubscriptions.length})</h2>
              <div className="space-y-4">
                {cancelledSubscriptions.map((subscription) => (
                  <Card key={subscription.subscription_id} className="p-6 border-border opacity-60">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-bold mb-1">{subscription.service_name}</h3>
                        <Badge variant="outline" className="bg-muted">
                          CANCELLED
                        </Badge>
                        <p className="text-sm text-muted-foreground mt-2">
                          Cancelled on: {format(new Date(subscription.cancelled_at), 'MMM dd, yyyy')}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold text-muted-foreground">
                          ${subscription.price.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Subscriptions;
